import type { ComputedRef } from 'vue';

import { useRafFn, useThrottleFn } from '@vueuse/core';
import { computed, onBeforeUnmount, onMounted, shallowRef } from 'vue';

/**
 * Placeholder digitado em loop no botão do dialog (listagem / variantes).
 */
export function useSearchSuggestDialogPlaceholderAnimation(
	effectivePlaceholders: ComputedRef<string[]>
) {
	const animatedPlaceholder = shallowRef<string>('');
	const placeholderIndex = shallowRef<number>(0);
	const isDeleting = shallowRef<boolean>(true);
	const isPaused = shallowRef<boolean>(false);
	const lastUpdateTime = shallowRef<number>(0);
	const isFirstRender = shallowRef<boolean>(true);
	const timeoutId = shallowRef<ReturnType<typeof setTimeout>>();

	const placeholderCount = computed<number>(() =>
		Array.isArray(effectivePlaceholders.value)
			? effectivePlaceholders.value?.length || 0
			: 0
	);

	const currentPlaceholder = computed<string>(() => {
		const index = placeholderIndex.value % placeholderCount.value;
		return effectivePlaceholders.value[index] || '';
	});

	const { pause, resume } = useRafFn(
		() => {
			if (!placeholderCount.value || isPaused.value) return;
			const now = performance.now();
			const elapsed = now - lastUpdateTime.value;
			if (elapsed >= calculateDelay()) {
				updatePlaceholderText();
				lastUpdateTime.value = now;
			}
		},
		{ immediate: false }
	);

	function calculateDelay(): number {
		const delay = isDeleting.value ? 75 / 2 : 75;
		return delay;
	}

	const updatePlaceholderText = useThrottleFn(async () => {
		if (isDeleting.value) {
			animatedPlaceholder.value = currentPlaceholder.value.substring(
				0,
				animatedPlaceholder.value.length - 1
			);
			if (animatedPlaceholder.value === '') {
				isPaused.value = true;
				timeoutId.value = setTimeout(() => {
					isPaused.value = false;
					isDeleting.value = false;
					placeholderIndex.value =
						(placeholderIndex.value + 1) % placeholderCount.value;
				}, 500);
			}
		} else {
			if (animatedPlaceholder.value === '') {
				animatedPlaceholder.value = currentPlaceholder.value.charAt(0);
			} else if (
				animatedPlaceholder.value.length < currentPlaceholder.value.length
			) {
				animatedPlaceholder.value = currentPlaceholder.value.substring(
					0,
					animatedPlaceholder.value.length + 1
				);
				if (animatedPlaceholder.value === currentPlaceholder.value) {
					isPaused.value = true;
					timeoutId.value = setTimeout(() => {
						isPaused.value = false;
						isDeleting.value = true;
					}, 1500);
				}
			}
		}
		isFirstRender.value = false;
	}, 16);

	function startAnimation() {
		animatedPlaceholder.value = isFirstRender.value
			? currentPlaceholder.value
			: '';
		isDeleting.value = isFirstRender.value ? true : false;
		placeholderIndex.value = 0;
		isPaused.value = false;
		lastUpdateTime.value = performance.now();
		resume();
	}

	function validateAndStartAnimation() {
		if (placeholderCount.value) {
			startAnimation();
		}
	}

	onBeforeUnmount(() => {
		if (timeoutId.value) {
			clearTimeout(timeoutId.value);
		}
	});

	return {
		animatedPlaceholder,
		currentPlaceholder,
		placeholderCount,
		validateAndStartAnimation,
		pause,
		resume
	};
}
